import ContactNameFields from '../shared/ContactNameFields';
import { newClientRef } from '../../lib/optimisticMessages';
import EventRecipientPicker from './EventRecipientPicker';
import BulkSendProgress from './BulkSendProgress';
import { validateConversationGroup } from '../../lib/groupConversations';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { onOpenTemplateSettingsRequest } from '../../lib/templateSettingsReturn';
import { useIsCompanyAdmin } from './useScheduledMessages';
import { supabaseClient } from '../../config/supabase';
import {
    buildNewContactPlaceholderName,
    parseRecipientList,
    contactMatchesSearch,
    contactReachOptions,
    describeChannelAvailability,
    findActiveThreadForContact,
    getContactPhoneNumber,
    isWhatsAppGeneralAllowed,
    normalizeOutboundPhoneAddress,
} from '../../lib/communicationsCompose';
import {
    buildTemplateVariableValues,
    fillTemplateBody,
    getTemplateDisplayName,
    getTemplateVariables,
} from '../../lib/communicationsTemplates';
import { formatAsYouType, formatPhone, phoneCountryName, toE164 } from '../../lib/phone';
import { threadBusinessNumber, validateNewGroup } from '../../lib/communicationRecipients';
import Checkbox from '../ui/Checkbox';
import Button3D from '../ui/Button3D';
import StatusChip from '../ui/StatusChip';
import CommunicationsThreadSidePanel from './CommunicationsThreadSidePanel';
import MessageComposerBox, { firstNameOnly } from './MessageComposerBox';
import { SupportWriteGuard } from '../../context/SupportSessionContext';
import HoverHelp from '../shared/HoverHelp';
import ContactCommunications from '../clients/ContactCommunications';
import { normalizeCommunicationPreferences, smsOptOutMessage } from '../../lib/smsConsent';
import { assembleSignature } from '../../lib/signatures';
import { signatureChipFor, useSignatureContext } from './useMessageSignature';
import { recipientTimeZoneForPhone } from '../../lib/scheduledSend';
import { resolveContactTimeZone } from '../../lib/contactTimeZone';
import { pickDefaultSendingIdentity, resolveThreadSendingNumber } from '../../lib/smsSenderSelection';
import { valuesForDeliveryTime, withCompanyName } from '../../lib/templateFieldResolver';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CHANNEL_LABELS = { sms: 'SMS', whatsapp: 'WhatsApp', email: 'Email' };
const OUTBOUND_MEDIA_BUCKET = 'inbound-message-media';
const PHONE_TYPING_RE = /^[+\d\s().-]+$/;

/**
 * BL-177 Step 2d (board 07): the recipient chip — "+49 163 5959331 · Germany · new" for a number (formatted by the one
 * phone helper, with its country), "name@example.com · new" for an email address.
 */
export function recipientChipText({ kind = '', address = '', isNew = true } = {}) {
    if (!address) return '';
    const parts = kind === 'phone' ? [formatPhone(address), phoneCountryName(address)] : [address];
    if (isNew) parts.push('new');
    return parts.filter(Boolean).join(' · ');
}

export function analyzeRecipient(value, { touched = false, hasMatches = false } = {}) {
    const trimmed = value.trim();
    const digits = trimmed.replace(/\D/g, '');
    const phoneLike = /^[+\d\s().-]+$/.test(trimmed);
    const hasAt = trimmed.includes('@');
    const emailInvalid = hasAt && (!EMAIL_RE.test(trimmed) || /\s/.test(trimmed) || (trimmed.match(/@/g) || []).length > 1);

    if (!trimmed) return { kind: '', valid: false, address: '', chip: '', error: '' };
    if (EMAIL_RE.test(trimmed)) return { kind: 'email', valid: true, address: trimmed, chip: 'Email', error: '' };
    if (phoneLike && (digits.length >= 10 || (trimmed.startsWith('+') && digits.length >= 8 && digits.length <= 15))) {
        // Stored as E.164 only: libphonenumber first, the older normaliser as the fallback for numbers it can't place.
        const normalizedPhone = toE164(trimmed) || normalizeOutboundPhoneAddress(trimmed);
        if (/^\+[1-9]\d{7,14}$/.test(normalizedPhone)) return { kind: 'phone', valid: true, address: normalizedPhone, chip: 'Phone', error: '' };
        return {
            kind: 'phone',
            valid: false,
            address: trimmed,
            chip: '',
            error: touched ? 'Enter a phone number with + country code, or a 10-digit US number.' : '',
        };
    }
    if (emailInvalid && (touched || /\s/.test(trimmed) || (trimmed.match(/@/g) || []).length > 1)) {
        return { kind: 'email', valid: false, address: trimmed, chip: '', error: 'Enter a valid email address or phone number.' };
    }
    if (phoneLike && digits.length > 0) {
        return {
            kind: 'phone',
            valid: false,
            address: trimmed,
            chip: '',
            error: touched ? 'Enter a complete phone number or email address.' : '',
        };
    }
    if (touched && !hasMatches) {
        return { kind: '', valid: false, address: trimmed, chip: '', error: 'Enter a valid email address or phone number.' };
    }
    return { kind: '', valid: false, address: trimmed, chip: '', error: '' };
}


// The thread side panel sits beside the composer only from 1200px (see .ia-comms-sidepanel in global.css). Without
// matchMedia (tests, old browsers) assume it fits, as before.
export function sidePanelFitsBeside() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia('(min-width: 1200px)').matches;
}

function ComposeNewMessagePanel({
    contacts = [],
    sessions = [],
    onCancel,
    onOpenThread,
    defaultEntityId = null,
    orgId = '',
    starting = false,
    error = '',
    allowSubjectLine = false,
    theme = 'light',
    autoSelectMessageType = true,
    userId,
    platformDoorContext = null,
    headerBrandEntityId = '',
    sendingIdentities = [],
    templates = [],
    templatesLoading = false,
    // Comms gate fixes: entity id -> its brand name (a template's "Company name" for the client being messaged).
    brandNameForEntity = null,
    onSendMessage,
    client = supabaseClient,
    // BL-177 Step 2d: "Schedule send…" (board 07, right side) is Step 3 — hidden until this is turned on.
    scheduleSendEnabled = false,
    onScheduleSend,
    // BL-177 Step 3: schedules this message instead of sending it now. Receives the same fields as onSendMessage plus
    // { at, cancelOnReply }; resolves true when it was scheduled. One person, or one group: bulk / campaign is not scheduled in v1.
    onScheduleMessage,
    // Comms next pass: the body / subject to start with (restored after a trip to Settings → Templates).
    initialBody = '',
    initialSubject = '',
}) {
    const dark = theme === 'dark';
    // BL-177 Step 2d: navy surfaces, white text (only timestamps are dim).
    const labelCls = dark ? 'text-white' : 'text-gray-600';
    const panelCls = dark ? 'bg-[#111B30] border-[#26365A] text-white' : 'bg-gray-50 border-gray-200';
    const cardCls = dark ? 'border-[#3B4D73] bg-[#0A1120]' : 'border-gray-200 bg-white';
    const inputCls = `w-full rounded-lg border px-3 py-2 text-sm ${dark
        ? 'bg-[#0A1120] border-[#3B4D73] text-white placeholder-[#A9B7D0]'
        : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'}`;
    const actionCls = 'px-2 py-1 rounded-md text-xs font-medium text-white bg-[var(--accent-ink)] disabled:opacity-50 disabled:cursor-not-allowed';
    const createContactPillCls = 'inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[var(--accent-ink)] bg-transparent px-2 py-1 text-xs font-medium text-[var(--accent-ink)]';
    const matchedContactTextCls = dark ? 'text-[var(--ink)]' : 'text-gray-800';
    const matchedContactDetailCls = dark ? 'text-[var(--ink)]' : labelCls;
    // Orange = check this (light orange on navy for >= 4.5:1 contrast).
    const checkMessageCls = 'mt-1 text-xs text-[#FDBA74]';

    const [toValue, setToValue] = useState('');
    const [subject, setSubject] = useState(() => (typeof initialSubject === 'string' ? initialSubject : ''));
    const [body, setBody] = useState(() => (typeof initialBody === 'string' ? initialBody : ''));
    // Keep the unsent message mounted while editing the matching template tab in the side panel.
    const openTemplateSettings = (channel, templateId = '') => {
        requestTemplatesPanel('edit', channel, templateId);
    };
    const canEditTemplates = useIsCompanyAdmin(client);
    const openTemplateSettingsRef = useRef(openTemplateSettings);
    openTemplateSettingsRef.current = openTemplateSettings;
    useEffect(() => onOpenTemplateSettingsRequest((detail) => openTemplateSettingsRef.current(detail.channel, detail.templateId)), []);
    const [selectedChannel, setSelectedChannel] = useState('');
    const [selectedIdentityId, setSelectedIdentityId] = useState('');
    const [identityExplicitlyChosen, setIdentityExplicitlyChosen] = useState(false);
    const [selectedRecipients, setSelectedRecipients] = useState([]);
    const [selectedTemplateSid, setSelectedTemplateSid] = useState('');
    const [templateVariables, setTemplateVariables] = useState({});
    const [sendNotice, setSendNotice] = useState('');
    const [recipientTouched, setRecipientTouched] = useState(false);
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);
    const [sidePanelOpen, setSidePanelOpen] = useState(false);
    const [templatePanelMode, setTemplatePanelMode] = useState('more');
    const [templatePanelChannel, setTemplatePanelChannel] = useState('');
    const [sidePanelTab, setSidePanelTab] = useState('ai_activity');
    const [attachmentError, setAttachmentError] = useState('');
    const [mediaUrl, setMediaUrl] = useState('');
    const [serviceUpdate, setServiceUpdate] = useState(false);
    const [eventId, setEventId] = useState('');
    const [bulkJobId, setBulkJobId] = useState('');
    useEffect(() => {
        try { setBulkJobId(globalThis.localStorage?.getItem(`comms-last-bulk:${orgId}:${userId || ''}`) || ''); } catch { /* storage is optional */ }
    }, [orgId, userId]);
    const [campaignEnabled, setCampaignEnabled] = useState(true);
    const [groupAvailable, setGroupAvailable] = useState(false);
    useEffect(() => {
        let active = true;
        if (!orgId || !client?.functions?.invoke || selectedRecipients.length < 2) return undefined;
        client.functions.invoke('staff-send-reply', { body: { orgId, action: 'group-capabilities', recipientAddress: '+14045550101' } })
            .then(({ data, error: failure }) => { if (active) setGroupAvailable(!failure && data?.groupMms === true); })
            .catch(() => { if (active) setGroupAvailable(false); });
        return () => { active = false; };
    }, [client, orgId, selectedRecipients.length]);
    const [groupChoice, setGroupChoice] = useState(''); // '' | 'group' | 'individual'
    const previousRecipientCountRef = useRef(0);
    const bulkRequestRef = useRef(null);

    const searchText = toValue.trim();
    const visibleContacts = useMemo(() => {
        // A number typed (and formatted as typed) searches by its digits, so "(404) 55" still finds 404-555-0143.
        const query = PHONE_TYPING_RE.test(searchText) && /\d/.test(searchText) ? searchText.replace(/\D/g, '') : searchText.trim();
        if (!query) return [];
        return contacts.filter((contact) => contactMatchesSearch(contact, query)).slice(0, 8);
    }, [contacts, searchText]);
    const recipient = useMemo(
        () => analyzeRecipient(toValue, { touched: recipientTouched, hasMatches: visibleContacts.length > 0 }),
        [recipientTouched, toValue, visibleContacts.length]
    );
    const displayAddress = recipient.kind === 'phone' ? formatPhone(recipient.address) : recipient.address;
    const placeholderName = useMemo(() => buildNewContactPlaceholderName(recipient.address), [recipient.address]);
    // BL-177 Step 2d (colour system): red means act now, so neither note is red. A bad format is orange "check this";
    // "No match found" is plain information (the message can still go), in neutral text.
    const recipientWarning = !recipient.valid && searchText && attemptedSubmit
        ? (recipient.error || 'Enter a complete phone number or email address.')
        : '';
    const recipientNote = recipient.valid && visibleContacts.length === 0 && searchText
        ? `No match found for ${displayAddress}. You can still send to this ${recipient.kind === 'email' ? 'address' : 'number'}.`
        : '';
    // F6: a valid number or email with no CRM match is a recipient already: no "Add" step is needed before sending.
    // Whether it is saved as a CRM contact afterwards is an optional choice (default: yes, as before).
    const [invalidEntries, setInvalidEntries] = useState([]);
    const [saveAsContact, setSaveAsContact] = useState(true);
    const directRecipient = useMemo(() => {
        if (selectedRecipients.length > 0 || !recipient.valid || visibleContacts.length > 0 || !selectedChannel) return null;
        const reachable = recipient.kind === 'email' ? selectedChannel === 'email' : selectedChannel !== 'email';
        const contact = {
            id: `new:${recipient.kind}:${recipient.address}`,
            name: placeholderName,
            phones: recipient.kind === 'phone' ? [recipient.address] : [],
            emails: recipient.kind === 'email' ? [recipient.address] : [],
            primary_account_id: null,
            entity_id: defaultEntityId,
            isNewRecipient: true,
            saveAsContact: saveAsContact && Boolean(defaultEntityId),
        };
        return {
            contact,
            reachable,
            option: { key: `${selectedChannel}:${recipient.address}`, channel: selectedChannel, label: CHANNEL_LABELS[selectedChannel] || selectedChannel, address: recipient.address },
        };
    }, [defaultEntityId, placeholderName, recipient, saveAsContact, selectedChannel, selectedRecipients.length, visibleContacts.length]);
    // BL-177 Step 2m: the recipient's communication choices belong to the client. When the recipient is ONE existing CRM contact, show
    // them read-only; a brand-new number has nothing to show (the client sets their own later).
    const existingRecipientContact = useMemo(() => {
        if (selectedRecipients.length > 1) return null;
        if (selectedRecipients.length === 1) return selectedRecipients[0].contact?.isNewRecipient ? null : selectedRecipients[0].contact;
        return visibleContacts.length === 1 ? visibleContacts[0] : null;
    }, [selectedRecipients, visibleContacts]);
    const [communicationPreferencesExpanded, setCommunicationPreferencesExpanded] = useState(false);
    useEffect(() => setCommunicationPreferencesExpanded(false), [existingRecipientContact?.id]);
    const existingPreferences = normalizeCommunicationPreferences(existingRecipientContact?.communication_preferences, existingRecipientContact || {});
    const textPreferencesOn = existingPreferences.trip_updates.text && !existingRecipientContact?.sms_opted_out_at;
    const effectiveRecipients = selectedRecipients.length > 0
        ? selectedRecipients.map((entry) => ({ ...entry, option: contactReachOptions(entry.contact, { sessions }).find((item) => item.channel === selectedChannel) || null }))
        : (directRecipient?.reachable ? [{ contact: directRecipient.contact, option: directRecipient.option }] : []);

    // The typed number/email counts for the pills before it is added, so a channel never vanishes on Add.
    const pillRecipients = useMemo(() => (selectedRecipients.length > 0 ? selectedRecipients
        : (recipient.valid && visibleContacts.length === 0
            ? [{ contact: { phones: recipient.kind === 'phone' ? [recipient.address] : [], emails: recipient.kind === 'email' ? [recipient.address] : [], isNewRecipient: true } }]
            : [])), [recipient.address, recipient.kind, recipient.valid, selectedRecipients, visibleContacts.length]);
    const channelStates = useMemo(() => Object.fromEntries(['sms', 'whatsapp', 'email'].map((channel) => [
        channel, describeChannelAvailability({ channel, recipients: pillRecipients, identities: sendingIdentities, sessions }),
    ])), [pillRecipients, sendingIdentities, sessions]);
    const availableChannels = ['sms', 'whatsapp', 'email'].filter((channel) => channelStates[channel].enabled);
    const visibleChannelOptions = ['sms', 'whatsapp', 'email'].filter((channel) => (
        sendingIdentities.some((identity) => identity.channel === channel)
        && (pillRecipients.length === 0 || pillRecipients.every(({ contact }) => (
            contactReachOptions(contact, { sessions }).some((option) => option.channel === channel)
        )))
    ));

    useEffect(() => {
        if (!autoSelectMessageType) return;
        const mainLineChannel = sendingIdentities.find((identity) =>
            identity.isMainLine && !identity.blockedReason && availableChannels.includes(identity.channel)
        )?.channel;
        const preferred = mainLineChannel || (availableChannels.includes('sms') ? 'sms' : (availableChannels[0] || ''));
        if (!selectedChannel || !availableChannels.includes(selectedChannel)) {
            setSelectedChannel(preferred);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSelectMessageType, availableChannels.join('|'), selectedChannel, sendingIdentities]);

    const identitiesForSelectedChannel = useMemo(
        () => sendingIdentities.filter((identity) => identity.channel === selectedChannel),
        [selectedChannel, sendingIdentities]
    );
    const primaryRecipient = effectiveRecipients[0]?.contact || null;
    const primaryThread = primaryRecipient ? findActiveThreadForContact(primaryRecipient, sessions) : null;
    const threadSendingNumber = selectedChannel === 'sms' && effectiveRecipients.length === 1 && primaryThread
        ? resolveThreadSendingNumber(
            primaryThread.external_thread_id,
            threadBusinessNumber(primaryThread),
            primaryThread?.raw_message_log,
            sendingIdentities.filter((line) => line.channel === 'sms')
        )
        : '';
    const defaultIdentity = pickDefaultSendingIdentity(identitiesForSelectedChannel, selectedChannel, threadSendingNumber);
    const selectedIdentity = identityExplicitlyChosen
        ? identitiesForSelectedChannel.find((identity) => identity.id === selectedIdentityId && !identity.blockedReason) || null
        : defaultIdentity;
    const channelNotice = selectedChannel === 'email'
        && recipient.kind === 'phone'
        && recipient.valid
        && visibleContacts.length === 0
        ? 'This contact has no email address for the selected channel.'
        : selectedChannel !== 'email'
            && recipient.kind === 'email'
            && recipient.valid
            && visibleContacts.length === 0
            ? 'This recipient has an email address, but the selected channel is not Email.'
            : selectedChannel === 'whatsapp' ? channelStates.whatsapp.hint || '' : '';

    const selectedTemplate = templates.find((template) => template.sid === selectedTemplateSid) || null;
    // Comms gate fixes: a template's "Company name" is the brand this message goes out for: the selected sending line's brand,
    // else the brand of the client being messaged. Never the last conversation opened elsewhere on the page.
    const templateCompanyName = (selectedIdentity?.entityName || '').trim()
        || (typeof brandNameForEntity === 'function' ? brandNameForEntity(primaryRecipient?.entity_id || '') : '')
        || '';
    // Picking another sending line (another brand) after the template is in: its "Company name" follows the new brand.
    useEffect(() => {
        if (!selectedTemplate) return;
        const hasCompanyField = Object.values(selectedTemplate.variables || {}).some((config) => config?.source === 'company.name');
        if (!hasCompanyField) return;
        setTemplateVariables((current) => {
            const next = withCompanyName(selectedTemplate, current, templateCompanyName);
            return Object.keys(next).every((key) => next[key] === current[key]) ? current : next;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateCompanyName, selectedTemplateSid]);
    // Comms gate fixes: WhatsApp → Templates lists the templates of the WhatsApp line this message would go out from.
    const whatsappTemplateIdentity = selectedChannel === 'whatsapp'
        ? selectedIdentity
        : pickDefaultSendingIdentity(sendingIdentities.filter((identity) => identity.channel === 'whatsapp' && !identity.blockedReason), 'whatsapp', '');
    const whatsappTemplateAccountSid = whatsappTemplateIdentity ? (whatsappTemplateIdentity.twilioAccountSid || null) : undefined;
    const templateIncomplete = Boolean(
        selectedTemplate
        && Object.keys(getTemplateVariables(selectedTemplate)).some((key) => !String(templateVariables[key] || '').trim())
    );
    const personalTemplateVariableKeys = Object.entries(selectedTemplate?.variables || {})
        .filter(([, config]) => typeof config?.source === 'string' && config.source.startsWith('contact.'))
        .map(([key]) => key);
    const personalTemplate = personalTemplateVariableKeys.length > 0;
    const missingPersonalRecipient = personalTemplate && effectiveRecipients.some(({ contact }) => {
        const values = buildTemplateVariableValues(selectedTemplate, { contact }, { useSampleFallback: selectedTemplate?.channel !== 'sms' });
        return personalTemplateVariableKeys.some((key) => !String(values[key] || '').trim());
    });
    // BL-177 Step 2d (section 10): apply the configured signature to each eligible outbound message.
    const signatureContext = useSignatureContext(
        client,
        orgId,
        userId,
        { platformDoorContext, headerBrandEntityId }
    );
    const [removeMine, setRemoveMine] = useState(false);
    const signature = signatureContext && selectedChannel
        ? assembleSignature({
            channel: selectedChannel,
            isTemplate: Boolean(selectedTemplateSid),
            isAutomatic: false,
            companySettings: signatureContext.companySettings,
            personalSignature: signatureContext.personalSignature,
            removeMine,
        })
        : null;
    const signatureApplies = Boolean(signature?.applies);
    const whatsappTemplateRequired = selectedChannel === 'whatsapp'
        && effectiveRecipients.length > 0
        && !effectiveRecipients.every(({ contact }) => !contact.isNewRecipient && isWhatsAppGeneralAllowed({ contact, sessions }));
    // A brand-new number has no name yet ("New contact (...)" is only a placeholder): no "Message New…".
    const placeholder = effectiveRecipients.length === 1 && !primaryRecipient?.isNewRecipient
        ? `Message ${firstNameOnly(primaryRecipient?.name, 'customer')}…`
        : 'Write a message…';
    // BL-177 Step 2e (6b): 2 or more phone recipients on an imported provider number can be ONE group text (everyone sees the replies)
    // or separate texts (the Campaign-style send). Nothing is assumed: the person chooses, and Send waits for the choice.
    const groupPhones = effectiveRecipients.map((entry) => entry.option?.address).filter((address) => /^\+[1-9]\d{7,14}$/.test(String(address || '')));
    const groupCheck = validateConversationGroup(groupPhones);
    const groupOffered = groupAvailable && selectedChannel === 'sms' && effectiveRecipients.length >= 2
        && groupPhones.length === effectiveRecipients.length && groupCheck.valid;
    const sendAsGroup = groupOffered && groupChoice === 'group';
    const showCampaignPill = effectiveRecipients.length > 1 && !sendAsGroup && !(groupOffered && !groupChoice);
    const effectiveMessageType = sendAsGroup
        ? 'group'
        : serviceUpdate ? 'service_update'
        : showCampaignPill && campaignEnabled
            ? 'campaign'
            : (selectedTemplateSid ? 'template' : 'general');
    const groupChoiceMissing = groupOffered && !groupChoice;
    const canSendBody = Boolean(body.trim() || (selectedTemplateSid && !templateIncomplete));
    const sendDisabled = starting
        || effectiveRecipients.length === 0
        || effectiveRecipients.some((entry) => !entry.option)
        || !selectedChannel
        || !selectedIdentity
        || Boolean(selectedIdentity.blockedReason)
        || signatureContext.loading
        || Boolean(signatureContext.error)
        || !canSendBody
        || (selectedChannel === 'email' && allowSubjectLine && !subject.trim() && !body.trim() && !selectedTemplateSid)
        || (whatsappTemplateRequired && !selectedTemplateSid)
        || groupChoiceMissing
        || (sendAsGroup && (!groupCheck.valid || (Boolean(selectedTemplateSid) && personalTemplate)))
        || (personalTemplate && (groupChoice === 'individual' || showCampaignPill) && missingPersonalRecipient);
    // Why Send is off, in plain English (shown under the composer). Only real gaps disable it.
    const sendDisabledReason = (() => {
        if (starting) return '';
        if (signatureContext.error) return signatureContext.error;
        if (signatureContext.loading) return 'Loading signature settings…';
        if (effectiveRecipients.length === 0) return 'Add a recipient to send.';
        if (effectiveRecipients.some((entry) => !entry.option)) return 'A recipient cannot receive this channel. Remove that pill or choose another channel.';
        if (!selectedChannel) return 'Choose SMS, WhatsApp or Email to send.';
        if (!selectedIdentity) return 'No sending number or address is set up for this channel.';
        if (selectedIdentity.blockedReason) return selectedIdentity.blockedReason;
        if (whatsappTemplateRequired && !selectedTemplateSid) return 'Choose an approved WhatsApp template to send.';
        if (selectedTemplateSid && templateIncomplete) return 'Fill in every template value to send.';
        if (groupChoiceMissing) return 'Choose Group text or Send individually.';
        if (sendAsGroup && !groupCheck.valid) return groupCheck.error;
        if (sendAsGroup && selectedTemplateSid && personalTemplate) return 'This template is personal. Send as a campaign (one personal text each) instead.';
        if (missingPersonalRecipient) return 'Add a first and last name for every recipient before sending this personal template.';
        if (!canSendBody) return 'Type a message to send.';
        if (sendDisabled) return 'Add a subject or a message to send.';
        return '';
    })();

    // The choice belongs to the people on this message: a change in who is on it asks again.
    const groupPeopleKey = groupPhones.join(',');
    useEffect(() => { setGroupChoice(''); }, [groupPeopleKey]);

    useEffect(() => {
        const previousCount = previousRecipientCountRef.current;
        if (previousCount <= 1 && selectedRecipients.length > 1) {
            setCampaignEnabled(true);
        }
        previousRecipientCountRef.current = selectedRecipients.length;
    }, [selectedRecipients.length]);

    const chooseChannel = (channel) => {
        setSelectedChannel(channel);
        setSelectedIdentityId('');
        setIdentityExplicitlyChosen(false);
        setSendNotice('');
        if (selectedTemplate && selectedTemplate.channel !== channel) {
            setSelectedTemplateSid('');
            setTemplateVariables({});
            if (sidePanelTab === 'templates') {
                setSidePanelOpen(false);
                setSidePanelTab('ai_activity');
            }
        }
    };

    const addAddresses = (value, labels = {}) => {
        const { added, invalid } = parseRecipientList(value, { contacts, selected: selectedRecipients, entityId: defaultEntityId });
        setSelectedRecipients((current) => [...current, ...added.map(({ contact }) => ({
            contact: contact.isNewRecipient && labels[contact.emails?.[0] || contact.phones?.[0]] ? { ...contact, name: labels[contact.emails?.[0] || contact.phones?.[0]] } : contact, option: contactReachOptions(contact, { sessions }).find((item) => item.channel === selectedChannel) || null,
        }))]);
        setInvalidEntries(invalid);
        setToValue('');
        setRecipientTouched(false);
    };
    const addUnknownRecipient = () => { if (recipient.valid) addAddresses(recipient.address); };

    const addRecipient = (contact) => {
        const option = contactReachOptions(contact, { sessions }).find((candidate) => candidate.channel === selectedChannel);
        if (!option || selectedRecipients.some((entry) => entry.contact.id === contact.id)) return;
        setSelectedRecipients((current) => [...current, { contact, option }]);
        setToValue('');
        setRecipientTouched(false);
        setSendNotice('');
    };

    const removeRecipient = (contactId) => {
        setSelectedRecipients((current) => current.filter((entry) => entry.contact.id !== contactId));
        setSendNotice('');
    };

    const openTemplatesPanel = (mode = 'more', channel = '') => {
        setTemplatePanelMode(mode); setTemplatePanelChannel(channel);
        setSidePanelOpen(true);
        setSidePanelTab('templates');
    };

    const chooseTemplate = (template) => {
        setSelectedTemplateSid(template?.sid || '');
        setTemplateVariables(buildTemplateVariableValues(
            template,
            { contact: primaryRecipient, trip: primaryThread, session: primaryThread, companyName: templateCompanyName },
            { useSampleFallback: template?.channel !== 'sms' }
        ));
    };

    const requestTemplatesPanel = openTemplatesPanel;

    const uploadAttachment = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        setAttachmentError('');
        if (!file) return;
        if (!orgId) {
            setAttachmentError('Could not upload the attachment without an organization scope.');
            return;
        }
        if (!['image/jpeg', 'image/png', 'image/gif', 'application/pdf'].includes(file.type) || file.size > 10 * 1024 * 1024) {
            setAttachmentError('Choose a JPG, PNG, GIF, or PDF file up to 10 MB.');
            return;
        }
        try {
            const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
            const objectPath = `${orgId}/compose-new/${newClientRef()}-${safeName}`;
            const { error: uploadError } = await client.storage.from(OUTBOUND_MEDIA_BUCKET).upload(objectPath, file, {
                contentType: file.type,
                upsert: false,
            });
            if (uploadError) throw uploadError;
            const { data, error: urlError } = await client.storage.from(OUTBOUND_MEDIA_BUCKET).createSignedUrl(objectPath, 3600);
            if (urlError || !data?.signedUrl) throw urlError || new Error('Could not prepare the attachment.');
            setMediaUrl(data.signedUrl);
            setSendNotice(`Attached ${file.name}`);
        } catch (uploadError) {
            setAttachmentError(uploadError?.message || 'Could not upload the attachment.');
        }
    };

    // BL-177 Step 3: "Schedule send…" for ONE person or ONE group. The typed body goes unsigned; the scheduler signs it when it really goes out.
    const [scheduling, setScheduling] = useState(false);
    const [scheduleError, setScheduleError] = useState('');
    const scheduleAvailable = Boolean(scheduleSendEnabled && onScheduleMessage)
        && !['campaign', 'service_update'].includes(effectiveMessageType) && (sendAsGroup || effectiveRecipients.length <= 1);
    const scheduleRecipientZone = sendAsGroup ? null : (resolveContactTimeZone(primaryRecipient) || (selectedChannel === 'email' ? null : recipientTimeZoneForPhone(effectiveRecipients[0]?.option?.address)));
    const scheduleComposedMessage = async ({ at, cancelOnReply }) => {
        setAttemptedSubmit(true);
        if (!onScheduleMessage || sendDisabled) {
            setScheduleError(sendDisabledReason || 'Finish the message before scheduling it.');
            return false;
        }
        setScheduleError('');
        setScheduling(true);
        // Comms gate fixes: the time-of-day greeting suits the delivery time, not the moment of writing.
        const deliveryValues = selectedTemplate ? valuesForDeliveryTime(selectedTemplate, templateVariables, { at }) : templateVariables;
        try {
            const result = await onScheduleMessage({
                recipients: effectiveRecipients,
                groupPhones: sendAsGroup ? groupCheck.phones : null,
                identity: selectedIdentity,
                sendingIdentityExplicitlyChosen: identityExplicitlyChosen,
                channel: selectedChannel,
                messageType: effectiveMessageType,
                subject: selectedChannel === 'email' ? subject : '',
                body: selectedTemplate ? fillTemplateBody(selectedTemplate.body || selectedTemplate.types?.['twilio/text']?.body, deliveryValues) : body,
                mediaUrl: mediaUrl.trim() || null,
                template: selectedTemplate,
                templateVariables: deliveryValues,
                templateBody: selectedTemplate?.body || selectedTemplate?.types?.['twilio/text']?.body || '',
                at,
                cancelOnReply,
            });
            if (!result) return false;
            if (directRecipient) {
                setToValue('');
                setRecipientTouched(false);
            }
            setSendNotice(result.notice || 'Scheduled.');
            setBody('');
            setSubject('');
            setMediaUrl('');
            setSelectedTemplateSid('');
            setTemplateVariables({});
            return true;
        } catch (failure) {
            setScheduleError(failure?.message || 'The message could not be scheduled.');
            return false;
        } finally {
            setScheduling(false);
        }
    };

    const sendComposedMessage = async () => {
        setAttemptedSubmit(true);
        if (!onSendMessage || sendDisabled) return;
        if (!bulkRequestRef.current) bulkRequestRef.current = newClientRef();
        const result = await onSendMessage({
            requestKey: bulkRequestRef.current,
            recipients: effectiveRecipients,
            eventId,
            groupPhones: sendAsGroup ? groupCheck.phones : null,
            identity: selectedIdentity,
            sendingIdentityExplicitlyChosen: identityExplicitlyChosen,
            channel: selectedChannel,
            messageType: effectiveMessageType,
            subject: selectedChannel === 'email' ? subject : '',
            body: selectedTemplate ? fillTemplateBody(selectedTemplate.body || selectedTemplate.types?.['twilio/text']?.body, templateVariables) : body,
            mediaUrl: mediaUrl.trim() || null,
            template: selectedTemplate,
            templateVariables,
            templateBody: selectedTemplate?.body || selectedTemplate?.types?.['twilio/text']?.body || '',
            // The typed body goes unsigned; the send path signs it per recipient (claim at send time for texts).
            signature: signatureApplies && !selectedTemplate ? signature : null,
        });
        if (result) {
            bulkRequestRef.current = null;
            if (result.jobId) { setBulkJobId(result.jobId); try { globalThis.localStorage?.setItem(`comms-last-bulk:${orgId}:${userId || ''}`, result.jobId); } catch { /* storage is optional */ } }
            setRemoveMine(false);
            if (directRecipient) {
                setToValue('');
                setRecipientTouched(false);
            }
            setSendNotice(result.notice || `${result.sent || 0} sent${result.skipped ? ` · ${result.skipped} skipped` : ''}.`);
            setBody('');
            setSubject('');
            setMediaUrl('');
            setSelectedTemplateSid('');
            setTemplateVariables({});
        }
    };

    return (
        <div className="relative flex h-full min-h-0 min-w-0">
            <div className={`h-full min-h-0 min-w-0 flex-1 rounded-xl border p-4 md:p-5 flex flex-col gap-4 ${panelCls}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="min-w-0 whitespace-nowrap text-xl font-bold text-white">New message</h3>
                    <Button3D variant="light" onClick={onCancel}>Cancel</Button3D>
                </div>
                {error && <p className="text-xs text-[#FCA5A5]" role="alert">{error}</p>}
                {bulkJobId && <BulkSendProgress client={client} orgId={orgId} jobId={bulkJobId} />}
                <EventRecipientPicker client={client} orgId={orgId} channel={selectedChannel} onEventChange={setEventId}
                    onAdd={(guests, id) => { setEventId(id); setServiceUpdate(true); addAddresses(guests.map((guest) => selectedChannel === 'email' ? guest.email || 'missing-email' : guest.phone || 'missing-phone').join(';'), Object.fromEntries(guests.map((guest) => [selectedChannel === 'email' ? guest.email : normalizeOutboundPhoneAddress(guest.phone || ''), [guest.first_name, guest.last_name].filter(Boolean).join(' ')]))); }} />
                <div className={`rounded-lg border px-3 py-3 ${cardCls}`}>
                    {selectedRecipients.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-1.5" aria-label="Selected recipients">
                            {selectedRecipients.map(({ contact }) => (
                                <button
                                    key={contact.id}
                                    type="button"
                                    onClick={() => removeRecipient(contact.id)}
                                    className={createContactPillCls}
                                    aria-label={`Remove ${contact.name}`}
                                >
                                    {contact.isNewRecipient
                                        ? `${contact.phones?.[0] ? recipientChipText({ kind: 'phone', address: contact.phones[0] }) : recipientChipText({ kind: 'email', address: contact.emails?.[0] || contact.name })}${contact.saveAsContact === false ? ' · not saved' : ''}`
                                        : contact.name} ×
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <label htmlFor="compose-to" className={`shrink-0 text-xs font-medium ${labelCls}`}>To:</label>
                        <input
                            id="compose-to"
                            type="text"
                            value={toValue}
                            onChange={(event) => {
                                // BL-177 Step 2d: a number formats as it's typed (+49 163 5959331); names and emails are
                                // left alone. Deleting never re-inserts the punctuation being removed.
                                const typed = event.target.value;
                                const deleting = typed.length < toValue.length;
                                setToValue(!deleting && PHONE_TYPING_RE.test(typed.trim()) && /\d/.test(typed)
                                    ? (formatAsYouType(typed).text || typed)
                                    : typed);
                            }}
                            onKeyDown={(event) => {
                                if (['Enter', ',', 'Tab'].includes(event.key) && toValue.trim()) {
                                    if (event.key !== 'Tab' || recipient.valid) event.preventDefault();
                                    if (recipient.valid) addUnknownRecipient(); else setRecipientTouched(true);
                                }
                            }}
                            onPaste={(event) => {
                                const value = event.clipboardData.getData('text');
                                if (/[,;\s]/.test(value.trim())) { event.preventDefault(); addAddresses(value); }
                            }}
                            onBlur={() => {}}
                            placeholder="Name, phone or email"
                            aria-label="Message recipient"
                            className={`${inputCls} min-w-[10rem] flex-1 border-0 px-0 py-1 focus:outline-none`}
                        />
                        {recipient.chip && (
                            <span data-recipient-chip="true" className="inline-flex min-w-0 max-w-full shrink items-center truncate rounded-full border border-[#2DD4BF] bg-transparent px-2.5 py-1 text-xs font-bold text-white">
                                {visibleContacts.length === 0 ? recipientChipText({ kind: recipient.kind, address: recipient.address }) : recipient.chip}
                            </span>
                        )}
                    </div>
                    {attemptedSubmit && invalidEntries.length > 0 && <p role="alert" className={checkMessageCls}>Invalid entries: {invalidEntries.join(', ')}</p>}
                    {selectedRecipients.map(({ contact }) => <div key={`prefs:${contact.id}`} className="mt-1 text-sm">
                        {contact.isNewRecipient && <Checkbox checked={contact.saveAsContact} disabled={!defaultEntityId}
                            onChange={(checked) => setSelectedRecipients((items) => items.map((item) => item.contact.id === contact.id
                                ? { ...item, contact: { ...item.contact, saveAsContact: checked } } : item))}>
                            Save {contact.emails?.[0] || contact.phones?.[0]} as contact · New
                        </Checkbox>}
                        {contact.isNewRecipient && contact.saveAsContact && <ContactNameFields
                            value={contact.first_name !== undefined ? contact.name : ''}
                            firstName={contact.first_name} lastName={contact.last_name}
                            className={inputCls} labelClassName={labelCls}
                            onChange={(name, parts) => setSelectedRecipients(items => items.map(item => item.contact.id === contact.id
                                ? { ...item, contact: { ...item.contact, name: name || buildNewContactPlaceholderName(contact.emails?.[0] || contact.phones?.[0]), first_name:parts.firstName, last_name:parts.lastName } } : item))}
                        />}
                        {selectedChannel && !contactReachOptions(contact, { sessions }).some((item) => item.channel === selectedChannel)
                            && <p role="status">{contact.name} cannot receive {CHANNEL_LABELS[selectedChannel]}.</p>}
                    </div>)}
                    {recipientWarning && <p className={checkMessageCls} role="alert">⚠ {recipientWarning}</p>}
                    {recipientNote && <p className="mt-1 text-xs text-white" role="status" data-recipient-note="true">{recipientNote}</p>}
                    {visibleContacts.length === 0 && recipient.valid && selectedRecipients.length === 0 && (
                        <Checkbox
                            className="mt-2 text-sm text-white"
                            checked={saveAsContact && Boolean(defaultEntityId)}
                            disabled={!defaultEntityId}
                            onChange={setSaveAsContact}
                        >
                            Save as a contact after sending{!defaultEntityId ? ' — not available: no company is set up' : ''}
                        </Checkbox>
                    )}
                    {recipient.valid && (
                        <div className="mt-2 flex items-center gap-2">
                            {(
                                <SupportWriteGuard mode="disable" tooltip="Adding a recipient is disabled in a read-only support session">
                                    <button
                                        type="button"
                                        disabled={starting || selectedRecipients.some((entry) => entry.contact.id === `new:${recipient.kind}:${recipient.address}`)}
                                        onClick={addUnknownRecipient}
                                        className={actionCls}
                                    >
                                        Add {displayAddress}
                                    </button>
                                </SupportWriteGuard>
                            )}
                        </div>
                    )}
                    {visibleContacts.length > 0 && (
                        <div className="mt-2 max-h-52 overflow-auto space-y-2">
                            {visibleContacts.map((contact) => {
                                const option = contactReachOptions(contact, { sessions }).find((candidate) => candidate.channel === selectedChannel);
                                return (
                                    <div key={contact.id} className={`ia-found-contact rounded-lg border p-2 ${cardCls}`}>
                                        <div className="min-w-0">
                                            <div className={`truncate text-sm font-medium ${matchedContactTextCls}`}>{contact.name}</div>
                                            <div className={`truncate text-xs ${matchedContactDetailCls}`}>
                                                {[formatPhone(normalizeOutboundPhoneAddress(contact.phones?.[0])) || getContactPhoneNumber(contact.phones?.[0]), contact.emails?.[0]].filter(Boolean).join(' · ')}
                                            </div>
                                        </div>
                                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                            {option ? (
                                                <button
                                                    type="button"
                                                    disabled={selectedRecipients.some((entry) => entry.contact.id === contact.id) || (selectedChannel === 'sms' && Boolean(contact.sms_opted_out_at))}
                                                    onClick={() => addRecipient(contact)}
                                                    className={actionCls}
                                                >
                                                    Add
                                                </button>
                                            ) : (
                                                <span className={`text-xs ${labelCls}`}>
                                                    {selectedChannel ? (selectedChannel === 'email' ? 'No email address for the selected Email channel.' : 'No phone number for the selected channel.') : 'Choose a sending channel first.'}
                                                </span>
                                            )}
                                            {selectedChannel === 'sms' && contact.sms_opted_out_at && (
                                                <span className="text-xs text-[#FCA5A5]">{smsOptOutMessage(contact.sms_opted_out_at)}</span>
                                            )}
                                        </div>
                                        {findActiveThreadForContact(contact, sessions) && <button type="button" className="ia-found-contact__thread text-xs underline" onClick={() => onOpenThread?.(findActiveThreadForContact(contact, sessions))}>Open existing conversation →</button>}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className={`min-h-0 flex-1 rounded-lg border ${cardCls}`}>
                    <div className="flex h-full min-h-0 flex-col justify-between">
                        <div className="flex-1 px-3 py-4">
                            {effectiveRecipients.length === 0 && (
                                <p className={`text-xs ${labelCls}`}>Start typing a name, phone or email above.</p>
                            )}
                            {selectedChannel === 'whatsapp' && selectedTemplate && (
                                // The editable fill-ins stay in the shared composer; this panel only summarizes approval.
                                <section className="ia-comms-template mt-3 min-w-0 rounded-2xl border border-[#2DD4BF] bg-[#0F2A2A] p-4" aria-label="WhatsApp template values" data-template-card="true">
                                    <div className="mb-3 flex min-w-0 flex-wrap items-center justify-between gap-2">
                                        <h4 className="min-w-0 text-base font-bold text-white">Template: {getTemplateDisplayName(selectedTemplate)}</h4>
                                        {selectedTemplate.status === 'approved' && <StatusChip tone="green">Approved</StatusChip>}
                                    </div>
                                    <p className="text-xs text-white/80">Fill-ins are shown in the reply box.</p>
                                </section>
                            )}
                        </div>
                        <div className="border-t border-transparent p-3">
                            {selectedIdentity?.blockedReason && (
                                <p role="alert" className="mb-2 text-xs text-[#FDBA74]">{selectedIdentity.blockedReason}</p>
                            )}
                            {selectedChannel === 'sms' && effectiveRecipients.length >= 2 && !groupOffered && <p role="status" className="mb-2 text-sm">
                                Send individually: {groupAvailable ? groupCheck.error : 'Messages will be sent individually.'}
                            </p>}
                            {groupOffered && (
                                <fieldset className="mb-3 min-w-0 rounded-xl border border-[var(--ia-line,#26365A)] p-3" data-testid="group-choice">
                                    <legend className="px-1 text-sm font-bold text-white">{effectiveRecipients.length} people on this text</legend>
                                    <div className="flex flex-col gap-2 text-sm text-white">
                                        <label className="flex cursor-pointer items-start gap-2">
                                            <input type="radio" name="group-choice" value="group" checked={groupChoice === 'group'} onChange={() => setGroupChoice('group')} className="mt-1" />
                                            <span><strong>Group text</strong> (everyone sees replies)<span className="block text-xs">One shared thread: everyone sees each other’s numbers and replies. Each message is delivered as MMS to every participant and billed per recipient.</span></span>
                                        </label>
                                        <label className="flex cursor-pointer items-start gap-2">
                                            <input type="radio" name="group-choice" value="individual" checked={groupChoice === 'individual'} onChange={() => setGroupChoice('individual')} className="mt-1" />
                                            <span><strong>Send individually</strong><span className="block text-xs">A separate text to each person; they can’t see each other.</span></span>
                                        </label>
                                    </div>
                                    {sendAsGroup && !groupCheck.valid && <p role="alert" className="mt-2 text-xs text-[#FDBA74]">{groupCheck.error}</p>}
                                </fieldset>
                            )}
                            {sendAsGroup && personalTemplate && (
                                <div className="mb-3 rounded-lg border border-amber-400/60 bg-amber-950/30 p-3 text-sm text-amber-100" role="alert" data-testid="personal-template-campaign-handoff">
                                    <p>This template is personal. Send as a campaign (one personal text each) instead?</p>
                                    <button
                                        type="button"
                                        className="ia-btn3d ia-btn3d--go mt-2"
                                        onClick={() => {
                                            setGroupChoice('individual');
                                            setCampaignEnabled(true);
                                        }}
                                    >
                                        Send as campaign
                                    </button>
                                </div>
                            )}
                            {effectiveRecipients.length > 1 && !sendAsGroup && <label className="mb-2 block text-sm">
                                <input type="checkbox" checked={serviceUpdate} onChange={(event) => setServiceUpdate(event.target.checked)} /> Event / service update — logistics only, not for promotions. Opt-outs still apply.
                            </label>}
                            <MessageComposerBox
                                theme={theme}
                                composeKey="new-message"
                                userId={userId}
                                body={body}
                                onBodyChange={setBody}
                                bodyPlaceholder={placeholder}
                                bodyAriaLabel="Message body"
                                channelNotice={channelNotice}
                                selectedChannel={selectedChannel}
                                channelOptions={visibleChannelOptions.map((channel) => ({
                                    value: channel,
                                    label: CHANNEL_LABELS[channel],
                                    disabled: !channelStates[channel].enabled,
                                    reason: channelStates[channel].reason || channelStates[channel].hint || '',
                                }))}
                                onChannelChange={chooseChannel}
                                showCampaignPill={showCampaignPill && !serviceUpdate}
                                campaignEnabled={campaignEnabled}
                                onCampaignToggle={(value) => { setServiceUpdate(false); setCampaignEnabled(value); }}
                                selectedIdentity={selectedIdentity}
                                channelIdentities={identitiesForSelectedChannel}
                                selectedIdentityId={identityExplicitlyChosen ? selectedIdentityId : ''}
                                onIdentityChange={(identityId) => {
                                    setSelectedIdentityId(identityId);
                                    setIdentityExplicitlyChosen(true);
                                }}
                                selectedTemplate={selectedTemplate}
                                selectedTemplateSid={selectedTemplateSid}
                                templateVariables={templateVariables}
                                onTemplateVariableChange={(key, value) => setTemplateVariables((current) => ({ ...current, [key]: value }))}
                                templateIncomplete={templateIncomplete}
                                templates={templates}
                                whatsappTemplateAccountSid={whatsappTemplateAccountSid}
                                onTemplateChoose={chooseTemplate}
                                onTemplateRemove={() => {
                                    setSelectedTemplateSid('');
                                    setTemplateVariables({});
                                }}
                                onTemplatePanelRequestOpen={requestTemplatesPanel}
                                onEditTemplates={openTemplateSettings}
                                canEditTemplates={canEditTemplates}
                                onUploadAttachment={uploadAttachment}
                                onInsertText={(kind) => setBody((current) => `${current}${current ? '\n' : ''}${kind === 'location' ? (primaryThread?.pickup_address || primaryThread?.pickup_location || '') : (primaryThread?.itinerary_short_form || '')}`)}
                                subject={subject}
                                onSubjectChange={setSubject}
                                showSubject={allowSubjectLine && selectedChannel === 'email'}
                                subjectAriaLabel="Message subject"
                                subjectPlaceholder="Optional subject"
                                subjectHelp={<HoverHelp helpId="communications.subject-line" userId={userId} />}
                                channelHelp={<HoverHelp helpId="communications.compose-channel" userId={userId} />}
                                sending={starting}
                                sendDisabled={starting || signatureContext.loading || Boolean(signatureContext.error)}
                                sendDisabledReason={signatureContext.error || (attemptedSubmit ? sendDisabledReason : '')}
                                onSend={sendComposedMessage}
                                counterSignature={signatureApplies ? signature : null}
                                signatureChip={signatureChipFor(signature, {
                        removeMine,
                        onToggle: () => setRemoveMine((current) => !current),
                        available: !signatureContext.loading && !signatureContext.error,
                    })}
                                helpAriaLabel="New message help"
                                scheduleSendEnabled={scheduleSendEnabled && !onScheduleMessage}
                                onScheduleSend={onScheduleSend}
                                schedule={scheduleAvailable ? {
                                    enabled: true, companyTimeZone: signatureContext?.timeZone || 'America/New_York', recipientTimeZone: scheduleRecipientZone,
                                    busy: scheduling, error: scheduleError, onConfirm: scheduleComposedMessage,
                                } : null}
                            />
                        </div>
                    </div>
                </div>
                {attachmentError && <p className="text-xs text-[#FCA5A5]" role="alert">{attachmentError}</p>}
                {sendNotice && <p className="text-xs text-[#86EFAC]">{sendNotice}</p>}
            </div>
            <CommunicationsThreadSidePanel
                    templatePanelMode={templatePanelMode} templatePanelChannel={templatePanelChannel}
                    client={client}
                canEditTemplates={canEditTemplates}
                open={sidePanelOpen}
                pinned={false}
                onToggleOpen={() => setSidePanelOpen((current) => !current)}
                onTogglePinned={() => {}}
                activeTab={sidePanelTab}
                onTabChange={(tab) => { setTemplatePanelMode('more'); setSidePanelTab(tab); }}
                selectedChannel={selectedChannel}
                onWhatsAppFlagChange={() => {}}
                theme={theme}
                intakeSession={primaryThread || { org_id: orgId, itinerary_short_form: '' }}
                contact={primaryRecipient}
                contactName={primaryRecipient?.isNewRecipient ? '' : (primaryRecipient?.name || '')}
                address={primaryRecipient?.emails?.[0] || getContactPhoneNumber(primaryRecipient?.phones?.[0]) || ''}
                templates={templatesLoading ? [] : templates}
                templateCompanyName={templateCompanyName}
                activeSendingIdentity={selectedIdentity}
                selectedTemplateSid={selectedTemplateSid}
                templateVariables={templateVariables}
                onTemplateSelect={(sid, defaults) => {
                    setSelectedTemplateSid(sid || '');
                    setTemplateVariables(defaults || {});
                }}
                onTemplateVariableChange={(key, value) => setTemplateVariables((current) => ({ ...current, [key]: value }))}
                hideTemplateFillIns
                currentUserId={userId}
            />
        </div>
    );
}

export default ComposeNewMessagePanel;
